(() => {
  const amsterdamToday = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const part = (type) => parts.find((item) => item.type === type)?.value;
    return `${part('year')}-${part('month')}-${part('day')}`;
  };
  const minutes = (time) => /^\d{1,2}:\d{2}$/.test(time || '')
    ? Number(time.split(':')[0]) * 60 + Number(time.split(':')[1]) : null;
  const dateText = (date) => new Date(`${date}T12:00:00Z`);
  const monthLabel = new Intl.DateTimeFormat('nl-NL', { month: 'long', timeZone: 'UTC' });
  const weekdayLabel = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', timeZone: 'UTC' });
  const displayName = (location) => {
    const name = String(location.name || '').replace(/\s+[-–]\s+/g, ' ').trim();
    return name.toLocaleLowerCase('nl-NL').startsWith(location.city.toLocaleLowerCase('nl-NL'))
      ? name : `${location.city} ${name}`.trim();
  };

  class BbAgenda {
    constructor(root) {
      this.root = root;
      this.shop = root.dataset.shop;
      this.cityFirst = root.dataset.loadMode === 'city';
      this.status = root.querySelector('[data-bb-agenda-status]');
      this.list = root.querySelector('[data-bb-agenda-list]');
      this.skeleton = root.querySelector('[data-bb-agenda-skeleton]');
      this.debug = root.querySelector('[data-bb-agenda-debug]');
      this.cityFilter = root.querySelector('[data-bb-agenda-city]');
      this.entries = [];
      this.diagnostics = [];
      this.apiFailures = 0;
      this.loadNonce = 0;
      try {
        const data = JSON.parse(root.querySelector('[data-bb-agenda-data]').textContent);
        this.locations = data.locations || [];
        this.editorialEvents = data.events || [];
      } catch (_error) {
        this.locations = [];
        this.editorialEvents = [];
      }
      this.showDebug = new URLSearchParams(location.search).get('agenda-debug') === '1';
      if (this.showDebug) this.debug.hidden = false;
      this.cityFilter.addEventListener('change', () => {
        if (this.cityFirst) this.loadSelected();
        else this.render();
      });
      if (this.cityFirst) this.render();
      else this.load(this.locations);
    }

    diagnose(location, reason) {
      this.diagnostics.push(`${location.city} · ${location.name}: ${reason}`);
      console.warn('[BB agenda]', location.name, reason);
    }

    async loadLocation(location, sharedProducts, today, isCurrent = () => true) {
      const probes = location.probe || [];
      if (!probes.length) {
        if (isCurrent()) this.diagnose(location, 'geen beschikbare boomvariant in de collectie');
        return [];
      }
      const byDate = new Map();
      let matched = false;
      let failed = false;
      for (const probe of probes) {
        try {
          const matches = await window.BbZapiet.matchingLocations(this.shop, [probe]);
          if (!isCurrent()) return [];
          const record = window.BbZapiet.resolve(
            matches, location.zapietId, location.name, sharedProducts.has(String(probe.product_id)),
          );
          if (!record) continue;
          matched = true;
          const schedule = await window.BbZapiet.pickupSchedule(this.shop, record.id, [probe], 12);
          if (!isCurrent()) return [];
          schedule.filter(({ date }) => date >= today).forEach(({ date, slots }) => {
            const existing = byDate.get(date) || [];
            slots.forEach((slot) => {
              if (!existing.some((item) => item.from === slot.from && item.until === slot.until)) existing.push(slot);
            });
            byDate.set(date, existing);
          });
        } catch (error) {
          if (!isCurrent()) return [];
          failed = true;
          this.apiFailures += 1;
          this.diagnose(location, `Zapiet API-fout: ${error?.message || 'onbekend'}`);
        }
      }
      if (!isCurrent()) return [];
      if (!matched && !failed) this.diagnose(location, 'geen eenduidige Zapiet-match');
      else if (matched && !byDate.size && !failed) this.diagnose(location, 'geen toekomstige dag met beschikbaar tijdslot');
      return [...byDate].map(([date, slots]) => ({
        date, slots, location, name: displayName(location), url: location.url,
      }));
    }

    mergeEditorialEvents(today) {
      this.editorialEvents.forEach((event) => {
        if (!event.name || !event.from || !event.url || event.until && event.until < event.from) return;
        let day = event.from;
        let count = 0;
        while (day <= (event.until || event.from) && count < 32) {
          if (day >= today) {
            const existing = this.entries.find((entry) => entry.date === day && entry.location?.handle === event.handle);
            if (existing) existing.labels = [...new Set([...(existing.labels || []), event.name])];
            else this.entries.push({
              date: day, slots: [], name: event.locationName, url: event.url,
              city: event.city, labels: [event.name], eventOnly: true,
            });
          }
          const next = new Date(`${day}T12:00:00Z`);
          next.setUTCDate(next.getUTCDate() + 1);
          day = next.toISOString().slice(0, 10);
          count += 1;
        }
      });
    }

    loadSelected() {
      const city = this.cityFilter.value;
      if (!city) {
        this.loadNonce += 1;
        this.entries = [];
        this.root.classList.add('is-awaiting-selection');
        this.skeleton.hidden = true;
        this.list.setAttribute('aria-busy', 'false');
        this.status.textContent = 'Kies een stad om de actuele ophaalmomenten te zien.';
        this.status.hidden = false;
        this.render();
        return;
      }
      this.load(city === '__all__' ? this.locations : this.locations.filter((location) => location.city === city));
    }

    async load(locations) {
      const nonce = ++this.loadNonce;
      this.root.classList.remove('is-awaiting-selection');
      this.root.classList.remove('is-ready');
      this.entries = [];
      this.diagnostics = [];
      this.apiFailures = 0;
      this.render();
      if (!window.BbZapiet || !locations.length) {
        this.status.textContent = 'De ophaalmomenten zijn nu niet beschikbaar. Bekijk de ophaallocaties voor meer informatie.';
        return;
      }
      this.status.textContent = 'Ophaalmomenten worden geladen…';
      this.status.hidden = false;
      this.skeleton.hidden = false;
      this.list.setAttribute('aria-busy', 'true');
      const counts = new Map();
      this.locations.forEach((location) => (location.probe || []).forEach((probe) => {
        const id = String(probe.product_id);
        counts.set(id, (counts.get(id) || 0) + 1);
      }));
      const sharedProducts = new Set([...counts].filter(([, count]) => count > 1).map(([id]) => id));
      const today = amsterdamToday();
      let completed = 0;
      try {
        for (let offset = 0; offset < locations.length && nonce === this.loadNonce; offset += 3) {
          await Promise.all(locations.slice(offset, offset + 3).map(async (location) => {
            const entries = await this.loadLocation(location, sharedProducts, today, () => nonce === this.loadNonce);
            if (nonce !== this.loadNonce) return;
            this.entries.push(...entries);
            completed += 1;
            this.status.textContent = `${completed} van ${locations.length} ophaallocaties gecontroleerd…`;
          }));
        }
        if (nonce !== this.loadNonce) return;
        this.mergeEditorialEvents(today);
        this.render();
        if (this.apiFailures) {
          this.status.textContent = 'Sommige ophaalmomenten zijn tijdelijk niet beschikbaar. Bekijk de locatiepagina voor actuele informatie.';
          this.status.hidden = false;
        } else if (this.entries.length) {
          this.status.textContent = `${this.entries.length} ${this.entries.length === 1 ? 'ophaalmoment' : 'ophaalmomenten'} geladen.`;
          this.status.hidden = false;
        } else {
          this.status.textContent = 'Er zijn nog geen boekbare ophaalmomenten bekend.';
          this.status.hidden = false;
        }
      } catch (error) {
        if (nonce !== this.loadNonce) return;
        console.warn('[BB agenda] Laden gestopt', error);
        this.status.textContent = 'De agenda kon niet volledig worden geladen. Bekijk een locatiepagina voor actuele informatie.';
        this.status.hidden = false;
      } finally {
        if (nonce === this.loadNonce) {
          this.skeleton.hidden = true;
          this.list.setAttribute('aria-busy', 'false');
          this.root.classList.add('is-ready');
          if (this.showDebug) this.debug.textContent = this.diagnostics.length
            ? this.diagnostics.join('\n') : 'Geen locaties overgeslagen.';
        }
      }
    }

    render() {
      const cities = [...new Set(this.locations.map((location) => location.city).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'nl'));
      const selectedCity = this.cityFilter.value;
      this.cityFilter.replaceChildren(new Option(this.cityFirst ? 'Kies een stad' : 'Alle steden', ''));
      cities.forEach((city) => this.cityFilter.add(new Option(city, city)));
      if (this.cityFirst) this.cityFilter.add(new Option('Alle steden laden (duurt langer)', '__all__'));
      this.cityFilter.value = selectedCity;
      const firstSlot = (entry) => Math.min(1440, ...entry.slots.map((slot) => minutes(slot.from) ?? 1440));
      const visible = this.entries.filter((entry) => !selectedCity || selectedCity === '__all__'
        || (entry.location?.city || entry.city) === selectedCity)
        .sort((a, b) => a.date.localeCompare(b.date)
        || firstSlot(a) - firstSlot(b)
        || a.name.localeCompare(b.name, 'nl'));
      const fragment = document.createDocumentFragment();
      let lastMonth = '';
      let lastDate = '';
      visible.forEach((entry) => {
        const month = entry.date.slice(0, 7);
        if (month !== lastMonth) {
          const heading = document.createElement('h2');
          heading.className = 'bb-agenda__month';
          heading.textContent = monthLabel.format(dateText(entry.date));
          fragment.append(heading);
          lastMonth = month;
        }
        const row = document.createElement('a');
        row.className = 'bb-agenda__row';
        row.href = `${entry.url}${entry.url.includes('?') ? '&' : '?'}view=pickup`;
        const day = document.createElement('span');
        day.className = 'bb-agenda__day';
        if (entry.date !== lastDate) {
          day.textContent = `${weekdayLabel.format(dateText(entry.date)).replace('.', '').toLocaleUpperCase('nl-NL')} ${Number(entry.date.slice(8))}`;
          lastDate = entry.date;
        } else day.setAttribute('aria-hidden', 'true');
        const content = document.createElement('span');
        content.className = 'bb-agenda__row-content';
        const name = document.createElement('span');
        name.className = 'bb-agenda__name';
        name.textContent = entry.name;
        content.append(name);
        if (entry.slots.length || entry.eventOnly) {
          const time = document.createElement('strong');
          time.className = 'bb-agenda__time';
          time.textContent = entry.eventOnly ? 'Evenement' : window.BbZapiet.formatPickupDay(entry.date, entry.slots)?.time || '';
          content.append(time);
        }
        (entry.labels || []).forEach((label) => {
          const pill = document.createElement('span');
          pill.className = 'bb-agenda__pill';
          pill.textContent = label;
          content.append(pill);
        });
        row.append(day, content);
        fragment.append(row);
      });
      this.list.replaceChildren(fragment);
    }
  }

  document.querySelectorAll('[data-bb-agenda]').forEach((root) => new BbAgenda(root));
})();
